/**
 * Spec 039 P3: the REST preview session behind the unchanged `PreviewSession`
 * seam. One request per logical change, no socket, no `tick` frames (Stage 3).
 *
 * Ordering is by this session's own request sequence, never by `overlay.rev`.
 * The session writes no terminal line; `apiFetch` already logs 5xx failures.
 * It is not wired into `quantApi.openPreview` until P5.
 */
import { ApiError } from '@/app/lib/apiError'
import { isQuantAbort, previewErrorKind, quantErrorMessage } from './quantErrors'
import { normalizePreviewResponse } from './previewResponse'
import { type ParamValue, type PreviewFrame, type PreviewRequestBody, type PreviewResponse, type PreviewSession, type PreviewSubscribe } from './types'

export const DEBOUNCE_MS = 400
export const PREVIEW_DOWN_RETRY_MS = 2500

export type RestPreviewSession = PreviewSession & {
  /** Starts a fresh logical request with the current params (the panel's Retry). */
  retry(): void
}

export interface RestPreviewDeps {
  post: (slug: string, body: PreviewRequestBody, signal: AbortSignal) => Promise<PreviewResponse>
}

/** Per logical request: which one-shot recoveries have been used. */
interface Attempt {
  downRetried: boolean
  supersededRetried: boolean
  mismatchRefetched: boolean
}

const FRESH: Attempt = { downRetried: false, supersededRetried: false, mismatchRefetched: false }

function missingFrom(error: unknown): { missing_days?: string[]; missing_count?: number } {
  if (!(error instanceof ApiError)) return {}
  const body = error.body
  if (typeof body !== 'object' || body === null) return {}
  const { missing_days, missing_count } = body as { missing_days?: unknown; missing_count?: unknown }
  const out: { missing_days?: string[]; missing_count?: number } = {}
  if (Array.isArray(missing_days)) out.missing_days = missing_days.filter((d): d is string => typeof d === 'string')
  if (typeof missing_count === 'number') out.missing_count = missing_count
  return out
}

export function createRestPreviewSession(args: PreviewSubscribe, deps: RestPreviewDeps): RestPreviewSession {
  const listeners = new Set<(frame: PreviewFrame) => void>()
  let closed = false
  let seq = 0
  let controller: AbortController | null = null
  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  /** Latest params the caller gave (sent or still pending). */
  let latestParams: Record<string, ParamValue> = args.params
  let sentKey = JSON.stringify(args.params)
  let candlesApplied = false
  let heldDataThrough: number | null = null

  function emit(frame: PreviewFrame): void {
    if (closed) return
    for (const cb of [...listeners]) cb(frame)
  }

  function clearDebounce(): void {
    if (debounceTimer !== null) clearTimeout(debounceTimer)
    debounceTimer = null
  }

  function clearRetry(): void {
    if (retryTimer !== null) clearTimeout(retryTimer)
    retryTimer = null
  }

  function send(params: Record<string, ParamValue>, attempt: Attempt): void {
    if (closed) return
    controller?.abort()
    clearRetry()
    const mine = new AbortController()
    controller = mine
    seq += 1
    const mySeq = seq
    const includeCandles = !candlesApplied
    sentKey = JSON.stringify(params)
    emit({ type: 'status', state: includeCandles ? 'loading' : 'computing' })
    const body: PreviewRequestBody = {
      instrument: args.instrument,
      tf: args.tf,
      bars: args.bars,
      params,
      include_candles: includeCandles,
    }
    deps
      .post(args.slug, body, mine.signal)
      .then((raw) => {
        if (closed || mySeq !== seq) return
        apply(raw, includeCandles, params, attempt)
      })
      .catch((error: unknown) => {
        if (closed || mySeq !== seq) return
        fail(error, includeCandles, params, attempt)
      })
  }

  function apply(raw: unknown, includeCandles: boolean, params: Record<string, ParamValue>, attempt: Attempt): void {
    let normalized
    try {
      normalized = normalizePreviewResponse(raw, includeCandles)
    } catch (error) {
      fail(error, includeCandles, params, attempt)
      return
    }
    const { meta, candles, overlay } = normalized
    if (candles === null && heldDataThrough !== null && meta.data_through !== heldDataThrough && !attempt.mismatchRefetched) {
      // The stored data moved under the held candles: refetch them once.
      candlesApplied = false
      send(params, { ...attempt, mismatchRefetched: true })
      return
    }
    emit({ type: 'meta', ...meta })
    if (candles !== null) {
      emit({ type: 'candles', seq: 0, total: candles.length, candles })
      heldDataThrough = meta.data_through
      candlesApplied = true
    }
    emit({ type: 'overlay', seq, ...overlay })
    emit({ type: 'status', state: 'live' })
  }

  function fail(error: unknown, includeCandles: boolean, params: Record<string, ParamValue>, attempt: Attempt): void {
    if (isQuantAbort(error)) return
    if (includeCandles) candlesApplied = false
    const kind = previewErrorKind(error)
    if (kind === 'abort') return
    if (kind === 'superseded') {
      if (!attempt.supersededRetried) {
        send(params, { ...attempt, supersededRetried: true })
        return
      }
      // A second `superseded` for the newest request: settle on what is held
      // (Ready) so the panel never stays on Loading or Updating forever. With
      // nothing held, fall through to a visible Unavailable.
      if (candlesApplied) {
        emit({ type: 'status', state: 'live' })
        return
      }
    }
    if (kind === 'down' && !attempt.downRetried) {
      clearRetry()
      retryTimer = setTimeout(() => {
        retryTimer = null
        send(params, { ...attempt, downRetried: true })
      }, PREVIEW_DOWN_RETRY_MS)
      return
    }
    const quant = error instanceof ApiError ? error : null
    emit({
      type: 'error',
      message: quantErrorMessage(error),
      code: quant?.code ?? null,
      status: quant?.status ?? 0,
      ...(kind === 'not_covered' ? missingFrom(error) : {}),
    })
  }

  // Listeners attach right after this function returns, so the first request
  // starts on the next microtask.
  void Promise.resolve().then(() => send(args.params, FRESH))

  return {
    onFrame(cb) {
      listeners.add(cb)
      return () => {
        listeners.delete(cb)
      }
    },
    sendParams(params) {
      if (closed) return
      const key = JSON.stringify(params)
      if (key === sentKey) {
        // Back to what was last sent: drop any pending edit.
        clearDebounce()
        latestParams = params
        return
      }
      latestParams = params
      clearRetry()
      clearDebounce()
      debounceTimer = setTimeout(() => {
        debounceTimer = null
        send(latestParams, FRESH)
      }, DEBOUNCE_MS)
    },
    retry() {
      if (closed) return
      clearDebounce()
      send(latestParams, FRESH)
    },
    close() {
      closed = true
      controller?.abort()
      clearDebounce()
      clearRetry()
      listeners.clear()
    },
  }
}
